import api from '@/lib/axios'

export interface ExtractionJob {
  id: string
  project_id: string
  status: string
  extraction_mode: string
  selected_model: string | null
  total_documents: number
  processed_documents: number
  failed_documents: number
  credits_estimated: number | null
  credits_consumed: number | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface ExtractedRow {
  id: string
  document_id: string
  field_id: string
  field_name: string | null
  raw_value: string | null
  parsed_value: Record<string, unknown> | null
  confidence: number | null
  page_reference: number | null
  status: string
  model_used: string | null
  job_id: string | null
  row_index: number | null
  is_archived: boolean
  created_at: string
}

export interface DocumentConfidenceSummary {
  document_id: string
  document_name: string
  avg_confidence: number | null
  min_confidence: number | null
  fields_extracted: number
  fields_total: number
  fields_with_value: number
}

export interface LlmCallLog {
  call_type: string
  model: string
  request_prompt: string
  raw_response: string
}

export interface ExtractionLog {
  model: string
  file_sent: boolean
  file_size_bytes: number
  text_fallback: boolean
  doc_fields_count: number
  line_items_found: number
  tokens_used: number
  duration_seconds: number
  warnings: string[]
  error: string | null
  llm_calls?: LlmCallLog[]  // only present for superadmins
}

export interface JobItem {
  id: string
  job_id: string
  document_id: string
  document_name: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  tokens_used: number | null
  llm_calls_made: number | null
  avg_confidence: number | null
  extraction_log: ExtractionLog | null
  summary: string | null
}

export interface PaginatedResults {
  items: ExtractedRow[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface CompactFieldValue {
  value: string | null
  confidence: number | null
}

export interface CompactRow {
  row_index: number | null
  fields: Record<string, CompactFieldValue>
}

export interface CompactDocumentResult {
  document_id: string
  document_name: string
  rows: CompactRow[]
}

export interface CompactPaginatedResponse {
  documents: CompactDocumentResult[]
  page: number
  page_size: number
  total_pages: number
}

export interface DocumentSummary {
  document_id: string
  document_name: string
  summary: string
}

export interface PlanLlmModel {
  id: string
  plan_id: string
  model_name: string
  display_name: string
  is_default: boolean
  credits_per_page_batch_mode: number
  chat_credits_per_message: number
  sort_order: number
  is_active: boolean
}

export interface FieldStat {
  field_name: string
  field_type: string
  avg_confidence: number | null
  null_rate: number
  total_values: number
}

export interface AnalyticsResponse {
  total_runs: number
  total_documents_processed: number
  total_failed: number
  total_tokens_used: number
  avg_confidence: number | null
  avg_duration_seconds: number | null
  extraction_success_rate: number
  models_used: Record<string, number>
  field_stats: FieldStat[]
  confidence_distribution: Record<string, number>
}

export const extractionApi = {
  initiate: (
    projectId: string,
    data: {
      document_ids: string[]
      field_ids?: string[]
      selected_model: string
      generate_summary?: boolean
    }
  ) =>
    api
      .post<ExtractionJob>('/extraction/jobs', data, { params: { project_id: projectId } })
      .then((r) => r.data),

  listJobs: (projectId: string) =>
    api
      .get<ExtractionJob[]>('/extraction/jobs', { params: { project_id: projectId } })
      .then((r) => r.data),

  getJob: (jobId: string) => api.get<ExtractionJob>(`/extraction/jobs/${jobId}`).then((r) => r.data),

  getJobItems: (jobId: string) =>
    api.get<JobItem[]>(`/extraction/jobs/${jobId}/items`).then((r) => r.data),

  getResults: (
    projectId: string,
    documentId?: string,
    jobId?: string,
    page: number = 1,
    pageSize: number = 100
  ) =>
    api
      .get<CompactPaginatedResponse>('/extraction/results', {
        params: {
          project_id: projectId,
          format: 'compact',
          page,
          page_size: pageSize,
          ...(documentId ? { document_id: documentId } : {}),
          ...(jobId ? { job_id: jobId } : {}),
        },
      })
      .then((r) => r.data),

  getResultsFull: (
    projectId: string,
    documentId?: string,
    jobId?: string,
    page: number = 1,
    pageSize: number = 100
  ) =>
    api
      .get<PaginatedResults>('/extraction/results', {
        params: {
          project_id: projectId,
          format: 'full',
          page,
          page_size: pageSize,
          ...(documentId ? { document_id: documentId } : {}),
          ...(jobId ? { job_id: jobId } : {}),
        },
      })
      .then((r) => r.data),

  getDocumentSummaries: (projectId: string, jobId?: string) =>
    api
      .get<DocumentSummary[]>('/extraction/results/summaries', {
        params: {
          project_id: projectId,
          ...(jobId ? { job_id: jobId } : {}),
        },
      })
      .then((r) => r.data),

  getResultsSummary: (projectId: string, jobId?: string) =>
    api
      .get<DocumentConfidenceSummary[]>('/extraction/results/summary', {
        params: {
          project_id: projectId,
          ...(jobId ? { job_id: jobId } : {}),
        },
      })
      .then((r) => r.data),

  estimate: (data: { document_ids: string[]; selected_model: string }) =>
    api
      .post<{
        estimated_credits: number
        rate_per_page: number
        total_pages: number
        model: string
        per_document: Array<{
          document_id: string
          document_name: string | null
          page_count: number
          credits: number
        }>
      }>('/extraction/estimate', data)
      .then((r) => r.data),

  stopJob: (jobId: string) =>
    api.post(`/extraction/jobs/${jobId}/stop`).then((r) => r.data),

  generateSummaries: (projectId: string, documentIds: string[]) =>
    api.post('/extraction/generate-summaries', { document_ids: documentIds }, { params: { project_id: projectId } }).then((r) => r.data),

  getSummaryProgress: (projectId: string) =>
    api.get<{ status: string; done: number; total: number; current_doc: string }>(
      '/extraction/summary-progress', { params: { project_id: projectId } }
    ).then((r) => r.data),

  getAnalytics: (projectId: string) =>
    api.get<AnalyticsResponse>('/extraction/analytics', { params: { project_id: projectId } }).then((r) => r.data),

  getPlanModels: () =>
    api.get<PlanLlmModel[]>('/billing/current-plan/models').then((r) => r.data),
}
